import { useState, useEffect } from 'react';
import { Flex, LocalVideo, RemoteVideo } from '../2022-component-library';
import { colors, fonts, PhotoCameraIcon } from '../ui';
import { usePostMessage, useReceiveMessage } from '../context/messageContext';
import { useDistanceDetector } from '../helpers/facial-recognition-hooks';
import { calibrationConstants } from '../helpers/calibration-constants';
import { useCalibration } from '../context/calibrationContext';
import { useTestContext } from '../context/testContext';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

// Full-screen notices shown over the patient's test stimulus while the distance is being
// (re)measured. IBISengage styling (exams task); the logic below decides when they show.
const glow = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.55; }
  50% { transform: scale(1.12); opacity: 0.25; }
`;

const Notice = styled.div`
  position: absolute;
  inset: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 28px;
  width: 100vw;
  height: 100vh;
  padding: 24px;
  box-sizing: border-box;
  background: ${colors.white};
  font-family: ${fonts.family};
  text-align: center;
  h2 {
    margin: 0;
    max-width: 720px;
    font-size: clamp(22px, 3.4vw, 40px);
    line-height: 1.3;
    font-weight: 700;
    color: ${colors.slate[700]};
  }
  p {
    margin: 0;
    max-width: 640px;
    font-size: clamp(15px, 2vw, 22px);
    line-height: 1.5;
    color: ${colors.slate[500]};
  }
`;

const Counter = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(96px, 14vw, 140px);
  aspect-ratio: 1;
  border-radius: 50%;
  background: ${({ tone }) => (tone === 'warning' ? colors.warning[500] : colors.primary)};
  color: #fff;
  font-size: clamp(36px, 6vw, 56px);
  font-weight: 800;
  &::before {
    content: '';
    position: absolute;
    inset: -14px;
    border-radius: 50%;
    background: inherit;
    animation: ${glow} 1.6s ease-in-out infinite;
    z-index: -1;
  }
  svg {
    width: 44%;
    height: 44%;
  }
`;

/**
 * DetectionMessage - presentational notice.
 * @param kind            'detecting' (first distance measurement) | 'moving' (movement detected)
 * @param movementCounter countdown from the distance detector; the number shows from 3 down
 */
export const DetectionMessage = ({ kind = 'detecting', movementCounter }) => {
  const showCount = movementCounter && movementCounter < 4;
  return (
    <Notice role='status' aria-live='polite' data-testid={`patient-${kind}-notice`}>
      <Counter tone={kind === 'moving' ? 'warning' : 'primary'} aria-hidden={!showCount}>
        {showCount ? movementCounter : <PhotoCameraIcon strokeWidth={1.8} />}
      </Counter>
      {kind === 'moving' ? (
        <>
          <h2>A movement has been detected.</h2>
          <p>Please remain still while the system recalibrates.</p>
        </>
      ) : (
        <>
          <h2>Please remain still, your distance is being detected.</h2>
          <p>Look at the screen and keep your head in the same position.</p>
        </>
      )}
      <p style={{ visibility: showCount ? 'visible' : 'hidden', fontWeight: 700, color: colors.slate[700] }}>Ready in {movementCounter}</p>
    </Notice>
  );
};

const DistanceDetectionMessage = ({ movementCounter }) => <DetectionMessage kind='detecting' movementCounter={movementCounter} />;

const MovementDetectedMessage = ({ movementCounter }) => <DetectionMessage kind='moving' movementCounter={movementCounter} />;

// REFACTOR NEEDED
// Optional props (calibration P5, additive): withVideo=false skips the hidden duplicate
// <LocalVideo/> when the page already shows the local tile (one <video id=sid> only);
// overlay=false hides the full-screen messages; onStatus({ counter, moving, hasInitialDistance })
// reports progress so the caller can render it.
const PatientFacialDetection = ({ withVideo = true, overlay = true, onStatus } = {}) => {
  const { testData } = useTestContext();
  const { movementNotification, start } = useDistanceDetector(testData.testMode === 'auto');
  const postMessage = usePostMessage();
  const messageFromOptician = useReceiveMessage();
  const [localMovementCounter, setLocalMovementCounter] = useState();
  const [patientIsMoving, setPatientIsMoving] = useState(false);
  const [hasInitialDistance, setHasInitialDistance] = useState(false);
  const [baseDistance, setBaseDistance] = useState(0);
  const {
    updateCalibrationData,
    calibrationData: { perceivedFocalLength, distanceFromScreen, methodSelected },
  } = useCalibration();

  useEffect(() => {
    if (hasInitialDistance) return;
    // If the method selected is the facial recognition (pupillary distance), we can assume we have
    // the perceived focal length and pass that to the distance detector.
    if (methodSelected === 'faceRecognition') {
      return start(0, true, perceivedFocalLength || calibrationConstants.perceivedFocalLength);
    } else {
      // If the method selected is not the facial recognition, we can assume the method
      // can be either blindspot or manual. In both cases, the perceived focal length
      // is calculdated after using the base distance and the new face width is detected.
      // We save the baseDistance to further use, in case the optician changes the testMode toggle.
      // (auto or off.)
      setBaseDistance(distanceFromScreen);
      return start(distanceFromScreen, false);
    }
  }, [distanceFromScreen, methodSelected, hasInitialDistance, perceivedFocalLength]);

  useEffect(() => {
    if (messageFromOptician?.type === 'recalculateDistance') setHasInitialDistance(false);
  }, [messageFromOptician]);

  useEffect(() => {
    if (movementNotification && !movementNotification.patientIsMoving) {
      setPatientIsMoving(false);
      setLocalMovementCounter(0);
      const { patientDistance: newPatientDistance } = movementNotification;
      setHasInitialDistance(true);
      updateCalibrationData({ distanceFromScreen: newPatientDistance });
      postMessage({ type: 'patientMovementDetected', content: movementNotification });
    }
  }, [movementNotification, updateCalibrationData, postMessage]);

  useEffect(() => {
    if (movementNotification && movementNotification.patientIsMoving) {
      const { counter: counterFromFacialDetection } = movementNotification;
      setLocalMovementCounter(counterFromFacialDetection);
      setPatientIsMoving(true);

      if (localMovementCounter === 0) {
        //this is the beginning of the "movement detected" phase
        postMessage({
          type: 'patientMovementDetected',
          content: movementNotification,
        });
      }
    }
  }, [movementNotification, localMovementCounter, postMessage]);

  useEffect(() => {
    onStatus?.({ counter: localMovementCounter, moving: patientIsMoving, hasInitialDistance });
  }, [onStatus, localMovementCounter, patientIsMoving, hasInitialDistance]);

  return (
    <>
      {withVideo && (
        <Flex zIndex='-1' position='absolute' style={{ width: '100px', height: '100px', overflow: 'hidden' }}>
          <LocalVideo />
          <RemoteVideo />
        </Flex>
      )}
      {!overlay ? null : methodSelected === 'faceRecognition' && testData.testMode === 'auto' && !hasInitialDistance ? (
        <DistanceDetectionMessage movementCounter={localMovementCounter} />
      ) : patientIsMoving && (hasInitialDistance || methodSelected !== 'faceRecognition') ? (
        <MovementDetectedMessage movementCounter={localMovementCounter} />
      ) : null}
    </>
  );
};

export default PatientFacialDetection;
